import { CONNECTIONS_PAGE_SIZE } from "@zapier/utils";
import {
  AuthenticationsOrder,
  AuthenticationsStatus,
  MultiFilterOperator,
  OwnerStatus,
} from "@zapier/bff";
import { useQueryAuthentications } from "./useQueryAuthentications";
import { useHydrateAuthenticationsWithImplementations } from "./useHydrateAuthenticationsWithImplementations";
import { useHydrateAuthenticationsWithOwners } from "./useHydrateAuthenticationsWithOwners";
import { useLimitOffsetFromPage } from "../../hooks/useLimitOffsetFromPage";

interface Input {
  excludeUnusable?: boolean;
  includeCompatible?: boolean;
  order?: AuthenticationsOrder;
  owner?: string;
  owners?: string;
  ownersOperator?: MultiFilterOperator;
  ownerStatus?: OwnerStatus;
  page?: number | null;
  pageSize?: number;
  search?: string;
  selectedApi?: string;
  apps?: string;
  appsOperator?: MultiFilterOperator;
  serviceSlug?: string;
  shared?: boolean;
  sharedByMe?: boolean;
  status?: AuthenticationsStatus;
  sharedWithAll?: boolean;
  isManaged?: boolean;
  isManagedAuthViolation?: boolean;
  isRestricted?: boolean;
}

export function useConnections({
  excludeUnusable,
  includeCompatible,
  order = "-updated_at",
  owner,
  owners,
  ownersOperator,
  ownerStatus,
  page = 1,
  pageSize = CONNECTIONS_PAGE_SIZE,
  search,
  selectedApi,
  apps,
  appsOperator,
  serviceSlug,
  shared,
  sharedByMe,
  sharedWithAll,
  status = "all",
  isManaged,
  isManagedAuthViolation,
  isRestricted,
}: Input) {
  const { limit, offset } = useLimitOffsetFromPage(page ?? 1, pageSize);

  const { totalCount, authentications, isLoading, isFetching } =
    useQueryAuthentications({
      excludeUnusable,
      includeCompatible,
      includeZapCount: true,
      limit,
      offset,
      ordering: order,
      owner,
      owners,
      ownersOperator,
      ownerStatus,
      search,
      selectedApi,
      apps,
      appsOperator,
      serviceSlug,
      shared,
      sharedByMe,
      sharedWithAll,
      status,
      isManaged,
      isManagedAuthViolation,
      isRestricted,
    });

  /** Hydrate auths with app implementation details *********************************************** */

  const {
    authentications: hydratedAuths,
    isLoading: isLoadingImplementations,
  } = useHydrateAuthenticationsWithImplementations({ authentications });

  /** Hydrate owner *********************************************** */

  const {
    authentications: hydratedAuthsWithMembers,
    isLoading: isLoadingOwners,
  } = useHydrateAuthenticationsWithOwners({ authentications: hydratedAuths });

  const totalPages = Math.ceil(totalCount / pageSize);

  return {
    totalPages,
    totalCount,
    connections: hydratedAuthsWithMembers,
    isLoading,
    isLoadingSecondary:
      isLoadingImplementations || isLoadingOwners || isLoading,
    isFetching,
  };
}
